import { Button, Card, Divider, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { SignUpTerminationParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

type SignUpTerminationDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const initialDateRange: SignUpTerminationDateRangeValue = {
	startDate: null,
	endDate: null,
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const buildSignUpTerminationParameters = ({
	dateRange,
	facilityHierarchy,
}: {
	dateRange: SignUpTerminationDateRangeValue;
	facilityHierarchy: FacilityHierarchyFilterValue;
}): SignUpTerminationParameters => {
	const parameters: SignUpTerminationParameters = {};

	if (dateRange.startDate) {
		parameters.startDate = dateRange.startDate.format('YYYY-MM-DD');
	}

	if (dateRange.endDate) {
		parameters.endDate = dateRange.endDate.format('YYYY-MM-DD');
	}

	applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

	return parameters;
};

export const SignUpTerminationReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<SignUpTerminationDateRangeValue>(initialDateRange);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);

	const reset = () => {
		setDateRange(initialDateRange);
		setFacilityHierarchy(initialFacilityHierarchy);
	};

	const validate = () => {
		if (dateRange.endDate && !dateRange.startDate) {
			onValidationError('Select a start date before selecting an end date.');
			return false;
		}

		if (dateRange.startDate && dateRange.endDate && dateRange.endDate.isBefore(dateRange.startDate, 'day')) {
			onValidationError('End date must be on or after the start date.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'signUpTermination',
			parameters: buildSignUpTerminationParameters({
				dateRange,
				facilityHierarchy,
			}),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							Date range
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker<Dayjs>
								label='Start date'
								value={dateRange.startDate}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker<Dayjs>
								label='End date'
								value={dateRange.endDate}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
